import React from 'react';
import { useNavigate } from 'react-router-dom';
import { SEOHead } from '../../components/seo/SEOHead';
import { FAQSchema, BreadcrumbSchema, ServiceSchema } from '../../components/seo/SchemaMarkup';
import { Breadcrumbs } from '../../components/seo/Breadcrumbs';
import { LatestArticles } from '../../components/blog/LatestArticles';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../../components/ui/accordion';
import { MapPin, Clock, Users, Crown, ArrowRight, Briefcase } from 'lucide-react';

export default function PrivatePartyVenueJamshedpur() {
  const navigate = useNavigate();

  const faqs = [
    {
      question: "Can I book Scratch Unleashed for a private event?",
      answer: "Yes! Scratch Unleashed is available for full venue buyouts on weekdays and select weekend slots. Private events include bachelor parties, bachelorette parties, corporate celebrations, product launches, and large group birthday parties. Contact us to discuss availability and packages."
    },
    {
      question: "How many people can Scratch Unleashed accommodate for a private event?",
      answer: "Scratch Unleashed can accommodate up to 200+ guests for private events. The venue's layout is flexible — you can book private sections within the main venue or the entire space. Our events team will help design the layout based on your guest count and event type."
    },
    {
      question: "Is Scratch Unleashed available for bachelor and bachelorette parties?",
      answer: "Absolutely! Bachelor and bachelorette parties are a speciality at Scratch Unleashed. We offer dedicated packages including private section booking, customised music programming, welcome shots or drinks for the party, and optional decorations. These events are handled with full discretion and fun."
    },
    {
      question: "Does Scratch Unleashed host corporate events?",
      answer: "Yes, Scratch Unleashed hosts corporate parties, team celebrations, product launches, and networking events. Our events team can customise the space, arrange catering, set up branded elements, and manage AV requirements. We've hosted events for 20-200+ corporate guests."
    },
    {
      question: "What is included in a private event package?",
      answer: "Private event packages typically include: reserved or full venue booking, dedicated events coordinator, custom music programming, bartender service, basic decor setup, and security. Add-ons include catering, premium decor, photo booth, custom cocktails, cake arrangements, and more."
    }
  ];

  const breadcrumbItems = [
    { name: 'Private Party Venue Jamshedpur', url: null }
  ];

  return (
    <div className="min-h-screen bg-[var(--bg)] text-[var(--text)]">
      <SEOHead
        title="Private Party Venue Jamshedpur | Corporate Events & Bachelor Party - Scratch Unleashed"
        description="Best private party venue in Jamshedpur. Host corporate events, bachelor parties, bachelorette parties & private celebrations at Scratch Unleashed. Venue buyouts available!"
        keywords="private party venue Jamshedpur, corporate event venue Jamshedpur, bachelor party Jamshedpur, bachelorette party Jamshedpur, private event space Jamshedpur, venue hire Jamshedpur, corporate party Jamshedpur, private nightclub booking Jamshedpur"
        canonical="https://scratchjsr.com/private-party-venue-jamshedpur"
      />
      <FAQSchema faqs={faqs} />
      <ServiceSchema
        serviceName="Private Party & Corporate Event Venue"
        serviceDescription="Premium private party and corporate event venue in Jamshedpur with full venue buyout options, custom packages, and professional event management."
        serviceUrl="https://scratchjsr.com/private-party-venue-jamshedpur"
      />
      <BreadcrumbSchema items={[
        { name: 'Home', url: 'https://scratchjsr.com/' },
        { name: 'Private Party Venue Jamshedpur', url: 'https://scratchjsr.com/private-party-venue-jamshedpur' }
      ]} />

      <div className="h-16"></div>

      <div className="max-w-[1120px] mx-auto px-4 sm:px-6 py-12">
        <Breadcrumbs items={breadcrumbItems} />

        <div className="mb-12">
          <h1 className="font-bebas text-5xl sm:text-6xl lg:text-7xl mb-4">
            PRIVATE PARTY VENUE JAMSHEDPUR
          </h1>
          <p className="text-xl text-[var(--text-dim)] mb-6">
            Bachelor Parties, Corporate Events & Private Celebrations at Scratch Unleashed
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <Button className="btn-crimson-glow rounded-xl" onClick={() => navigate('/checkin')}>
              Book Private Event
              <ArrowRight size={16} className="ml-2" />
            </Button>
            <Button variant="outline" className="rounded-xl border-[var(--border-custom)]" onClick={() => navigate('/birthday-party-venue-jamshedpur')}>
              Birthday Packages
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2 space-y-8">
            <section>
              <h2 className="font-bebas text-3xl mb-4">Jamshedpur's Premier Private Event Venue</h2>
              <div className="space-y-4 text-[var(--text-dim)] leading-relaxed">
                <p>
                  When you need the best <strong>private party venue in Jamshedpur</strong>, Scratch Unleashed stands in a class of its own. Our venue combines premium nightclub production — professional sound, lighting, bar service — with the flexibility to host completely private, customised events. From intimate groups of 20 to large celebrations of 200+, we create events that your guests talk about for years.
                </p>
                <p>
                  Available for weekday private bookings and select weekend slots, Scratch Unleashed gives you an exclusive experience not possible in conventional banquet halls. Our private event packages are designed to deliver the excitement of a premium nightclub experience while ensuring your event feels personal and tailored to you.
                </p>
              </div>
            </section>

            <section>
              <h2 className="font-bebas text-3xl mb-4">Bachelor & Bachelorette Parties</h2>
              <div className="space-y-4 text-[var(--text-dim)] leading-relaxed">
                <p>
                  Looking for the perfect <strong>bachelor party venue in Jamshedpur</strong>? Scratch Unleashed is the top choice. Our dedicated packages for bachelor and bachelorette parties include a private section with your own table host, welcome shots for the full party, customisable DJ requests, photo opportunities, and optional add-ons like sash/tiara packages and cake arrangements.
                </p>
                <p>
                  Bachelor party packages start from ₹15,000 for groups of 8-10, covering premium table booking, two bottles of spirits, mixers, and your own section of the venue. <strong>Bachelorette party packages</strong> are similarly designed with attention to creating a fun, safe, and unforgettable last night out.
                </p>
              </div>
            </section>

            <section>
              <h2 className="font-bebas text-3xl mb-4">Corporate Events & Team Parties</h2>
              <div className="space-y-4 text-[var(--text-dim)] leading-relaxed">
                <p>
                  Scratch Unleashed has become Jamshedpur's go-to <strong>corporate event venue</strong> for companies looking to celebrate in style. Whether you're recognising team achievements, hosting client entertainment, or organising an annual party, our venue delivers an experience that reflects well on your company.
                </p>
                <p>
                  Corporate packages include branding opportunities (logo on displays, custom printed materials), AV setup support, catering options, and dedicated event management. Our team handles logistics so your HR or events team can focus on attendance and experience. Minimum group bookings of 30 are recommended for a truly immersive private corporate event.
                </p>
              </div>
            </section>

            <section className="bg-[var(--surface)] border border-[var(--border-custom)] rounded-2xl p-8">
              <h2 className="font-bebas text-3xl mb-4">Plan Your Private Event</h2>
              <p className="text-[var(--text-dim)] mb-6">
                Contact us to discuss your event requirements. Our events team will create a customised proposal within 24 hours.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Button className="btn-crimson-glow rounded-xl" onClick={() => navigate('/checkin')}>
                  Enquire Now
                </Button>
                <Button variant="outline" className="rounded-xl border-[var(--border-custom)]" onClick={() => navigate('/contact')}>
                  Contact Us
                </Button>
              </div>
            </section>

            <section>
              <h2 className="font-bebas text-3xl mb-6">Frequently Asked Questions</h2>
              <Accordion type="single" collapsible className="space-y-3">
                {faqs.map((faq, index) => (
                  <AccordionItem key={index} value={`item-${index}`} className="border border-[var(--border-custom)] rounded-xl px-4 bg-[var(--surface)]">
                    <AccordionTrigger className="hover:no-underline text-left">
                      <span className="font-semibold">{faq.question}</span>
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-[var(--text-dim)] pb-4">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </section>
          </div>

          <div className="lg:col-span-1 space-y-6">
            <Card className="p-6 bg-[var(--surface)] border border-[var(--border-custom)] rounded-xl">
              <h3 className="font-bebas text-2xl mb-4">Private Events Info</h3>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <Users size={20} className="text-[var(--accent-gold)] mt-1" />
                  <div>
                    <p className="font-semibold text-sm">Capacity</p>
                    <p className="text-sm text-[var(--text-dim)]">20 to 200+ guests</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Clock size={20} className="text-[var(--accent-gold)] mt-1" />
                  <div>
                    <p className="font-semibold text-sm">Availability</p>
                    <p className="text-sm text-[var(--text-dim)]">Weekdays (full buyout) + select weekend slots</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <MapPin size={20} className="text-[var(--accent-gold)] mt-1" />
                  <div>
                    <p className="font-semibold text-sm">Location</p>
                    <p className="text-sm text-[var(--text-dim)]">3rd Floor, Bindal Mall, Sonari, Jamshedpur</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Crown size={20} className="text-[var(--accent-gold)] mt-1" />
                  <div>
                    <p className="font-semibold text-sm">Starting Price</p>
                    <p className="text-sm text-[var(--text-dim)]">Packages from ₹15,000</p>
                  </div>
                </div>
              </div>
            </Card>

            <LatestArticles maxPosts={3} />

            <Card className="p-6 bg-[var(--surface)] border border-[var(--border-custom)] rounded-xl">
              <h3 className="font-bebas text-2xl mb-4">Related Pages</h3>
              <div className="space-y-3">
                <button onClick={() => navigate('/birthday-party-venue-jamshedpur')} className="w-full text-left text-sm text-[var(--accent-gold)] hover:underline">
                  → Birthday Party Venue
                </button>
                <button onClick={() => navigate('/vip-table-booking-jamshedpur')} className="w-full text-left text-sm text-[var(--accent-gold)] hover:underline">
                  → VIP Table Booking
                </button>
                <button onClick={() => navigate('/dj-night-jamshedpur')} className="w-full text-left text-sm text-[var(--accent-gold)] hover:underline">
                  → DJ Nights
                </button>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
